<!--
  Admin App.svelte — Root application shell for the management app.

  Always renders the sidebar + navbar layout.  Unauthenticated
  visitors are redirected to the public login page via a full
  page navigation (cross-app redirect).
-->
<script lang="ts">
	import '../app.css';
	import { onMount } from 'svelte';
	import {
		Navbar,
		NavBrand,
		NavHamburger,
		Sidebar,
		SidebarGroup,
		SidebarItem,
		SidebarWrapper,
		CloseButton,
		Alert,
		Spinner,
		Button,
		Img
	} from 'flowbite-svelte';
	import { SvelteToast } from '@zerodevx/svelte-toast';
	import { sineIn } from 'svelte/easing';
	import { slide } from 'svelte/transition';
	import { Heading, P } from 'flowbite-svelte';

	import Router, { push } from '../abstract-spa-router';
	import { location } from '../abstract-spa-router';

	import routes from './routes';

	import site from '../site';

	import { isConfigurationLoaded, configurationError, configurationStore } from '../AppConfiguration';
	import {
		isAuthorizationPending,
		isAuthorized,
		isAdmin,
		isLoggingOut,
		colorTheme,
		unreadNotificationCount
	} from '../Auth';

	/**
	 * Synchronous token check for optimistic rendering.
	 * When a JWT exists in storage we render the Router immediately
	 * instead of showing a spinner while auth resolves — the Dashboard
	 * reads the token via `getToken()` so API calls work right away.
	 * If the token turns out to be expired, the 401 handler in
	 * `unwrap()` triggers a redirect to `/login` as usual.
	 */
	const hasToken: boolean = !!localStorage.getItem('auth_token');
	import { getSupportDeskStats, getSupportOverview, listMyApplications } from '../AppNetwork';
	import type { Application, SupportDeskStats } from '../AppTypes';
	import Drawer from '../components/Drawer.svelte';
	import AbstractIdCard from '../components/AbstractIDCard.svelte';
	import LanguageSelector from '../components/LanguageSelector.svelte';
	import ThemeSwitcherButton from '../components/ThemeSwitcherButton.svelte';
	import { storable } from '../storable';
	import { t } from '../i18n';
	interface Props {
		children?: import('svelte').Snippet;
	}

	let { children }: Props = $props();

	// ── Responsive sidebar state ──────────────────────────────

	/** Breakpoint at which the sidebar switches from overlay to persistent. */
	const breakPoint: number = 640;
	/** Current window width in pixels. */
	let width: number = $state(breakPoint);
	/** Whether the sidebar backdrop is enabled (mobile only). */
	let isSidebarBackdropEnabled: boolean = $state(true);
	/** Whether clicking outside the sidebar closes it (mobile only). */
	let isSidebarHideOnClickOutside = $state(true);
	/** Whether the sidebar drawer is hidden (mobile default). */
	let drawerHidden: boolean = $state(false);

	/**
	 * Rail mode: the sidebar collapses to a 60px strip of icons.
	 *
	 * Everything in the strip — brand badge, toggle, nav icons — sits on the
	 * rail's centre axis. That requires labels to be `hidden` rather than merely
	 * transparent: an invisible label still occupies width and would push the
	 * icon off centre. Each item carries a `title`, which is the only label left
	 * once the text is gone. The choice persists per browser.
	 */
	const isSidebarRail = storable<boolean>('sidebar.isRail', false);

	/** Collapsed state of the Administration section, persisted per browser. */
	const isAdminSectionOpen = storable<boolean>('sidebar.isAdminSectionOpen', true);

	// ── Sidebar dynamic data ──────────────────────────────────

	/** Visible applications to show in the sidebar (isActive + isAlwaysVisible). */
	let sidebarApps: Application[] = $state([]);

	/** Custom sidebar links from platform settings. */
	let customSidebarLinks: {
		label: string;
		url: string;
		iconImageName: string | null;
		iconURL: string | null;
		order: number;
	}[] = $state([]);

	/** Load visible applications for sidebar display (user-scoped). */
	async function loadSidebarApps(): Promise<void> {
		try {
			const userApps = await listMyApplications();
			sidebarApps = userApps
				.filter(
					(ua) =>
						ua.application &&
						ua.application.isActive &&
						(ua.application.isAlwaysVisible ||
							(ua.application.whenRegistered && ua.isUserPermissionGranted))
				)
				.map((ua) => ua.application!);
		} catch {
			/* non-critical — sidebar just won't show apps */
		}
	}

	/** Load custom sidebar links from configuration store (public, no auth needed). */
	function loadCustomLinks(): void {
		const config = $configurationStore;
		if (config?.customSidebarLinks && Array.isArray(config.customSidebarLinks)) {
			customSidebarLinks = config.customSidebarLinks;
		}
	}

	/**
	 * Unified sidebar entry — either a registered application or a custom link.
	 * Sorted by `order` for final display.
	 */
	type SidebarEntry =
		| { type: 'app'; label: string; href: string; iconURL: string | null; order: number }
		| { type: 'link'; label: string; href: string; iconURL: string | null; order: number };

	/** Merged and sorted list of sidebar apps + custom links. */
	let mergedSidebarItems: SidebarEntry[] = $derived.by(() => {
		const appEntries: SidebarEntry[] = sidebarApps.map((app, i) => ({
			type: 'app' as const,
			label: app.applicationName,
			href: app.website || '#',
			iconURL: app.iconImageName ? `${site.backend.baseURL}/uploads/applications/${app.iconImageName}` : null,
			/* Applications default to index-based order (0, 1, 2…) for backward compat. */
			order: i
		}));

		const linkEntries: SidebarEntry[] = customSidebarLinks.map((link) => ({
			type: 'link' as const,
			label: link.label,
			href: link.url,
			iconURL: link.iconURL ?? null,
			order: link.order
		}));

		return [...appEntries, ...linkEntries].sort((a, b) => a.order - b.order);
	});

	/* Responsive sidebar toggles — show sidebar on wide screens. */
	$effect(() => {
		if (width >= breakPoint) {
			drawerHidden = false;
			isSidebarHideOnClickOutside = false;
			isSidebarBackdropEnabled = false;
		} else {
			drawerHidden = true;
			isSidebarHideOnClickOutside = true;
			isSidebarBackdropEnabled = true;
		}
	});

	onMount(() => {
		if (width >= breakPoint) {
			drawerHidden = false;
			isSidebarHideOnClickOutside = false;
		} else {
			drawerHidden = true;
			isSidebarHideOnClickOutside = true;
		}
	});

	/* Load sidebar data whenever user becomes authorized.
	   Apps come from the user-scoped profile endpoint;
	   custom links re-derive whenever the configuration store updates
	   (it may load after auth resolves). */
	$effect(() => {
		if ($isAuthorized) {
			loadSidebarApps();
			loadSupportNav();
		}
	});

	$effect(() => {
		if ($isAuthorized && $configurationStore) {
			loadCustomLinks();
		}
	});

	/**
	 * Toggle sidebar visibility on mobile.
	 * Optionally navigate to a route via the router.
	 */
	const fnToggleSide = (event?: Event, href: any = undefined) => {
		if (width < breakPoint) {
			drawerHidden = !drawerHidden;
		}
		if (href) {
			event?.preventDefault();
			push(href);
		}
	};

	/** Open the sidebar drawer (hamburger click). */
	const fnToggleDrawer = () => {
		drawerHidden = false;
	};

	// ── Route awareness ───────────────────────────────────────

	/** Active URL path for sidebar highlight (first path segment). */
	let activeUrl: string = $derived($location.substring(0, $location.indexOf('/', 1)) || $location);

	// ── Licence sections ──────────────────────────────────────

	/**
	 * The licence page's three sections, each a real route onto the one page.
	 *
	 * They are one page because a licence is why you may have a build, the build
	 * is what people came for, and the documentation is what you read afterwards
	 * — facts that answer one question and belong on one screen. They are three
	 * routes because each is somewhere a reader can be sent, bookmark and come
	 * back to, which is what the sidebar is naming.
	 */
	const licenseSections = [
		{ path: '/Licenses', label: 'Licenses', icon: 'fa-key' },
		{ path: '/Downloads', label: 'Downloads', icon: 'fa-download' },
		{ path: '/Documentation', label: 'Documentation', icon: 'fa-book' }
	];

	/** The first path segment, which is what a licence section route is named by. */
	let licenseSectionPath: string = $derived(`/${$location.split('/')[1] ?? ''}`);

	/** Whether the page on screen is one of the three licence sections. */
	let isLicenseSection: boolean = $derived(licenseSections.some((s) => s.path === licenseSectionPath));

	/**
	 * The product whose licences are on screen, when the URL names one.
	 *
	 * Taken off the URL rather than tracked, so that switching sections from a
	 * licence detail page keeps the product the reader was looking at instead of
	 * dropping them back on the first one in the list.
	 */
	let licenseAppUUID: string = $derived(isLicenseSection ? ($location.split('/')[2] ?? '') : '');

	/** Destination for a section row, keeping the current product selected. */
	function licenseSectionHref(path: string): string {
		return `${path}${licenseAppUUID ? `/${licenseAppUUID}` : ''}`;
	}

	/**
	 * Which section row is lit.
	 *
	 * Passed explicitly rather than left to flowbite's `href === activeUrl` test,
	 * which compares the whole href: a row pointing at `/Downloads/<uuid>` would
	 * never match the trimmed `/Downloads` the sidebar is given.
	 */
	function isLicenseSectionActive(path: string): boolean {
		return licenseSectionPath === path;
	}

	// ── Support ───────────────────────────────────────────────

	/**
	 * Whether the signed-in user is a support agent, which is what earns the
	 * Support Desk row. Taken from the support overview rather than the JWT's
	 * roles: the support role is a setting that can change under a live token,
	 * and the overview is the server's current answer.
	 */
	let isSupportAgent: boolean = $state(false);

	/**
	 * Whether the support desk is switched on. Assumed on until the overview says
	 * otherwise: switching support off is rare, and a row that vanishes then is
	 * better than one that pops in on every page load.
	 */
	let isSupportEnabled: boolean = $state(true);

	/** The desk's headline counts, for the Support Desk badge. `null` until they arrive. */
	let deskStats = $state<SupportDeskStats | null>(null);

	/**
	 * Learns whether the user is an agent, and if so loads the desk counts.
	 *
	 * Best-effort: a failure leaves the user seeing the Support row alone, which
	 * is what every non-agent sees anyway.
	 */
	async function loadSupportNav(): Promise<void> {
		/** Neither call is worth an error on the shell. */
		try {
			const overview = await getSupportOverview();
			/* Only an explicit `false` hides support, as on the server. */
			isSupportEnabled = overview.isEnabled !== false;
			isSupportAgent = isSupportEnabled && overview.isAgent;
			if (isSupportAgent) await loadDeskStats();
		} catch {
			isSupportAgent = false;
		}
	}

	/** Re-reads the desk counts. A failure keeps the last ones rather than clearing the badge. */
	async function loadDeskStats(): Promise<void> {
		/** The badge is a convenience, never a reason to fail. */
		try {
			deskStats = await getSupportDeskStats();
		} catch {
			/* keep the last counts */
		}
	}

	/** The first two path segments, which is what the support rows are told apart by. */
	let supportSegments: string[] = $derived($location.split('/').slice(1, 3));

	/** Whether the page on screen is the agent desk (any page under `/Support/Desk`). */
	let isSupportDeskPage: boolean = $derived(supportSegments[0] === 'Support' && supportSegments[1] === 'Desk');

	/** Whether the page on screen is the customer side of support. */
	let isSupportPage: boolean = $derived(supportSegments[0] === 'Support' && !isSupportDeskPage);

	/**
	 * The Support Desk badge: open tickets nobody has picked up.
	 *
	 * Unassigned rather than "awaiting agent", because an unassigned ticket is
	 * the one no agent will otherwise notice — an assigned one already has
	 * someone watching it. Breaching tickets turn the badge red.
	 */
	let deskBadge: number = $derived(deskStats?.unassigned ?? 0);

	/* The badge follows the desk: refreshed whenever the agent moves between
	   desk pages, which is when they have just changed what it counts. */
	$effect(() => {
		if (isSupportAgent && isSupportDeskPage) loadDeskStats();
	});

	/**
	 * Redirect unauthenticated users to the public login page.
	 * Full page navigation because login is a separate SPA.
	 * Skip when logout is already in progress to avoid flicker.
	 */
	$effect.pre(() => {
		if (!$isLoggingOut && !$isAuthorizationPending && !$isAuthorized) {
			window.location.href = '/login';
		}
	});
</script>

<svelte:window bind:innerWidth={width} />

<header class="mx-auto w-full flex-none">
	<Navbar
		class="border-primary-500 dark:border-primary-500 bg-depth border-t-[4px] border-solid"
		hidden={!drawerHidden}
	>
		<NavHamburger
			onclick={fnToggleDrawer}
			class="lg:hidden} m-0 mr-3 rounded-lg p-1.5 whitespace-normal hover:bg-gray-100 focus:ring-2 focus:ring-gray-400 focus:outline-none dark:hover:bg-gray-600"
		/>
		<NavBrand href="/Manage/" class="lg:ml-64">
			<Img class="h-[30px] dark:invert" src="/LogoTextWide-Black.png" alt={site.name} />
		</NavBrand>
		<div class="ml-auto flex items-center gap-1">
			<!-- Language selector -->
			<LanguageSelector buttonClass="h-9 p-2" />
			<!-- Dark mode toggle -->
			<button
				type="button"
				aria-label={$t('Toggle dark mode')}
				class="inline-block rounded-lg p-2.5 text-sm text-black hover:bg-gray-100 hover:text-gray-900 dark:text-white dark:hover:bg-gray-700"
				onclick={() => {
					$colorTheme = $colorTheme === 'dark' ? 'light' : 'dark';
				}}
			>
				<i class="fa-solid {$colorTheme === 'dark' ? 'fa-sun' : 'fa-moon'} h-5 w-5"></i>
			</button>
		</div>
	</Navbar>
</header>

<Drawer
	divClass="overflow-y-auto z-40 bg-depth"
	transitionParams={{ x: -250, duration: 200, easing: sineIn }}
	backdrop={isSidebarBackdropEnabled}
	bind:hidden={drawerHidden}
	activateClickOutside={isSidebarHideOnClickOutside}
	width={$isSidebarRail ? 'w-[60px]' : 'w-[250px]'}
	class="overflow-scroll pb-32"
	id="sidebar"
>
	<div class="flex items-center">
		<CloseButton onclick={() => (drawerHidden = true)} class="mt-2 -mb-4 text-white sm:hidden" />
	</div>
	<Sidebar
		class="w-full !bg-transparent"
		divClass="!p-0 !bg-transparent"
		{activeUrl}
		position="static"
		disableBreakpoints
		backdrop={false}
	>
		<!-- `relative` anchors the collapse toggle. In rail mode the horizontal
		     padding halves, so the brand badge still fits inside a 60px strip and
		     can sit on the same centre line as the 20px nav icons. -->
		<SidebarWrapper
			class="relative overflow-x-hidden overflow-y-auto rounded !bg-transparent py-4 {$isSidebarRail
				? 'px-2'
				: 'px-4'}"
		>
			{@const railCenter = $isSidebarRail ? 'justify-center' : ''}
			{@const activeClass = `flex items-center ${railCenter} p-1.5 text-sm font-semibold rounded-lg dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700 text-primary-500 bg-primary-100 dark:bg-primary-600/20 my-0.5 active`}
			{@const nonActiveClass = `flex items-center ${railCenter} p-1.5 text-sm font-medium text-gray-700 rounded-lg dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 my-0.5`}
			<!-- No `h-5` or `pt-1`: on an inline `<i>` those shift the glyph off the
			     row's centre line, which is invisible beside a label but glaring in
			     rail mode's column of bare icons. The active row colours itself, so
			     the icon just inherits. -->
			{@const iconClass = 'w-5 min-w-5 shrink-0 transition'}
			{@const sectionTitleClass =
				'ml-2 mt-4 text-sm font-medium flex justify-between hover:text-primary-500 text-black dark:text-white cursor-pointer'}
			<!-- `hidden`, not `opacity-0`: an invisible label still occupies width,
			     which would push the icon off the rail's centre line. -->
			{@const labelClass = `ml-3 truncate ${$isSidebarRail ? 'hidden' : ''}`}

			<!-- Brand row, as in AbstractWebStarter and SalesEdge: a square gradient
			     badge beside the app name set in the brand gradient. The badge is
			     the part that survives rail mode — it is already on the nav icons'
			     centre axis, and a wordmark cannot fit a 60px strip. The trailing
			     spacer reserves the slot where an app-level action button sits in
			     the templates; it is dropped on the rail, where it would push the
			     badge off centre. -->
			<div class="flex items-center gap-2 text-lg" class:justify-center={$isSidebarRail}>
				<a title={site.name} class="flex items-center gap-2 text-lg" href="/Manage/">
					<div
						class="from-primary-500 shadow-primary-500/30 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br to-orange-500 text-white shadow-md"
					>
						<i class="fa-solid fa-id-badge text-gray-200 dark:text-zinc-800"></i>
					</div>
					{#if !$isSidebarRail}
						<span class="text-gradient-primary font-bold uppercase">
							{site.shortname}
						</span>
					{/if}
				</a>
				{#if !$isSidebarRail}
					<div class="grow"></div>
				{/if}
			</div>

			<!-- Collapse toggle. Tucked into the top-right corner when expanded;
			     becomes its own full-width row in rail mode, where there is no
			     corner to spare.

			     An inline SVG rather than a Font Awesome glyph: `h-4` does nothing
			     to an inline `<i>`, so the glyph aligns on its baseline and sits
			     high in the button. An SVG honours the box and centres properly.
			     The mark is a panel with an arrow, which reads as "sidebar" far
			     better than a bare chevron.

			     The `!` on the padding is load-bearing: flowbite's Button carries
			     `px-5`, and Tailwind emits the shorthand `.p-0` before the
			     directional `.px-5`, so the plain form loses on source order
			     however the class attribute is written. -->
			<Button
				onclick={() => ($isSidebarRail = !$isSidebarRail)}
				color="alternative"
				title={$isSidebarRail ? $t('Expand sidebar') : $t('Collapse sidebar')}
				class="grow-0 border-none bg-transparent !p-0 text-xs text-gray-600 dark:!bg-transparent dark:text-white {$isSidebarRail
					? 'mt-2 w-full'
					: 'absolute top-2 right-4'}"
			>
				<svg
					class="h-4 w-4"
					aria-hidden="true"
					xmlns="http://www.w3.org/2000/svg"
					width="24"
					height="24"
					fill="none"
					viewBox="0 0 24 24"
				>
					<path
						stroke="currentColor"
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d={$isSidebarRail
							? 'm6 10 1.99994 1.9999-1.99994 2M11 5v14m-7 0h16c.5523 0 1-.4477 1-1V6c0-.55228-.4477-1-1-1H4c-.55228 0-1 .44772-1 1v12c0 .5523.44772 1 1 1Z'
							: 'M7.99994 10 6 11.9999l1.99994 2M11 5v14m-7 0h16c.5523 0 1-.4477 1-1V6c0-.55228-.4477-1-1-1H4c-.55228 0-1 .44772-1 1v12c0 .5523.44772 1 1 1Z'}
					/>
				</svg>
			</Button>

			<!-- Separates the brand from the nav. The identity card used to sit
			     here and carried the gap; it now lives at the foot of the rail. -->
			<div class="mt-4">&nbsp;</div>

			{#if !$configurationError}
				<SidebarGroup class="appNav mt-2">
					<SidebarItem
						{activeClass}
						{nonActiveClass}
						spanClass={labelClass}
						title={$t('Dashboard')}
						label={$t('Dashboard')}
						href="/"
						onclick={(event) => fnToggleSide(event, '/')}
					>
						{#snippet icon()}
							<i class="fa-solid fa-house {iconClass}"></i>
						{/snippet}
					</SidebarItem>

					<!-- Licences sit beside the dashboard rather than inside Settings:
					     they are what a user comes here to look at, not something they
					     came to configure. Three rows, three routes, one page — each
					     opens its own section, so the sidebar names what is on it
					     rather than leaving the reader to scroll for it. -->
					{#each licenseSections as section (section.path)}
						<SidebarItem
							{activeClass}
							{nonActiveClass}
							spanClass={labelClass}
							title={$t(section.label)}
							label={$t(section.label)}
							href={licenseSectionHref(section.path)}
							active={isLicenseSectionActive(section.path)}
							onclick={(event) => fnToggleSide(event, licenseSectionHref(section.path))}
						>
							{#snippet icon()}
								<i class="fa-solid {section.icon} {iconClass}"></i>
							{/snippet}
						</SidebarItem>
					{/each}

					<!-- Support sits with the licence rows: it is part of what a licence
					     buys. Shown to everyone, eligible or not — the page is where a
					     customer learns why they cannot file a ticket, and a missing row
					     would explain nothing. -->
					{#if isSupportEnabled}
					<SidebarItem
						{activeClass}
						{nonActiveClass}
						spanClass={labelClass}
						title={$t('Support')}
						label={$t('Support')}
						href="/Support"
						active={isSupportPage}
						onclick={(event) => fnToggleSide(event, '/Support')}
					>
						{#snippet icon()}
							<i class="fa-solid fa-life-ring {iconClass}"></i>
						{/snippet}
					</SidebarItem>
					{/if}

					<!-- The agent's own entry. Its badge counts unassigned tickets and is
					     hidden on the rail, where it would push the icon off the centre
					     line — the count moves into the tooltip instead. -->
					{#if isSupportAgent}
						<SidebarItem
							{activeClass}
							{nonActiveClass}
							spanClass={labelClass}
							title={deskBadge > 0
								? $t('Support Desk — {{count}} unassigned', { count: deskBadge })
								: $t('Support Desk')}
							label={$t('Support Desk')}
							href="/Support/Desk"
							active={isSupportDeskPage}
							onclick={(event) => fnToggleSide(event, '/Support/Desk')}
						>
							{#snippet icon()}
								<i class="fa-solid fa-headset {iconClass}"></i>
							{/snippet}
							{#snippet subtext()}
								{#if deskBadge > 0 && !$isSidebarRail}
									<span
										class="ms-auto rounded-full px-2 text-xs font-semibold {(deskStats?.breaching ?? 0) > 0
											? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
											: 'bg-primary-100 text-primary-700 dark:bg-primary-600/20 dark:text-primary-300'}"
										data-testid="support-desk-badge"
									>
										{deskBadge}
									</span>
								{/if}
							{/snippet}
						</SidebarItem>
					{/if}
				</SidebarGroup>

				{#if $isAdmin}
					<!-- The heading has nowhere to live on a 60px rail, so it hides and
					     its group is forced open — an item under a collapsed heading
					     would otherwise be unreachable, with no heading left to click. -->
					<!-- svelte-ignore a11y_click_events_have_key_events -->
					<!-- svelte-ignore a11y_no_static_element_interactions -->
					<div
						class:hidden={$isSidebarRail}
						class="{sectionTitleClass} {!$isAdminSectionOpen ? 'opacity-50' : ''}"
						onclick={() => ($isAdminSectionOpen = !$isAdminSectionOpen)}
					>
						{$t('Administration')}
						<i class="fa-solid {$isAdminSectionOpen ? 'fa-angle-down' : 'fa-angle-up'}"></i>
					</div>
					{#if $isAdminSectionOpen || $isSidebarRail}
						<div transition:slide>
							<SidebarGroup class="appNav mt-0">
								<SidebarItem
									{activeClass}
									{nonActiveClass}
									spanClass={labelClass}
									title={$t('Users')}
									label={$t('Users')}
									href="/Users"
									onclick={(event) => fnToggleSide(event, '/Users')}
								>
									{#snippet icon()}
										<i class="fa-solid fa-users {iconClass}"></i>
									{/snippet}
								</SidebarItem>
								<SidebarItem
									{activeClass}
									{nonActiveClass}
									spanClass={labelClass}
									title={$t('Applications')}
									label={$t('Applications')}
									href="/Applications"
									onclick={(event) => fnToggleSide(event, '/Applications')}
								>
									{#snippet icon()}
										<i class="fa-solid fa-cubes {iconClass}"></i>
									{/snippet}
								</SidebarItem>
								<SidebarItem
									{activeClass}
									{nonActiveClass}
									spanClass={labelClass}
									title={$t('Roles')}
									label={$t('Roles')}
									href="/Roles"
									onclick={(event) => fnToggleSide(event, '/Roles')}
								>
									{#snippet icon()}
										<i class="fa-solid fa-shield-halved {iconClass}"></i>
									{/snippet}
								</SidebarItem>
								<SidebarItem
									{activeClass}
									{nonActiveClass}
									spanClass={labelClass}
									title={$t('Newsletters')}
									label={$t('Newsletters')}
									href="/Newsletters"
									onclick={(event) => fnToggleSide(event, '/Newsletters')}
								>
									{#snippet icon()}
										<i class="fa-solid fa-newspaper {iconClass}"></i>
									{/snippet}
								</SidebarItem>
							</SidebarGroup>
						</div>
					{/if}
				{/if}

				<!-- Merged Applications + Custom Sidebar Links (sorted by order) -->
				{#if mergedSidebarItems.length > 0}
					<SidebarGroup class="appNav mt-0">
						{#each mergedSidebarItems as entry (entry.type + entry.label)}
							<SidebarItem
								{activeClass}
								{nonActiveClass}
								spanClass={labelClass}
								title={entry.label}
								label={entry.label}
								href={entry.href}
								target="_blank"
							>
								{#snippet icon()}
									{#if entry.iconURL}
										<img
											src={entry.iconURL}
											alt=""
											class="h-5 min-h-5 w-5 min-w-5 shrink-0 rounded object-contain dark:invert"
										/>
									{:else}
										<i class="fa-solid {entry.type === 'app' ? 'fa-cube' : 'fa-link'} {iconClass}"
										></i>
									{/if}
								{/snippet}
							</SidebarItem>
						{/each}
					</SidebarGroup>
				{/if}

				<SidebarGroup class="appNav mt-0">
					<SidebarItem
						{activeClass}
						{nonActiveClass}
						spanClass={labelClass}
						title={$t('Settings')}
						label={$t('Settings')}
						href="/Settings"
						onclick={(event) => fnToggleSide(event, '/Settings')}
					>
						{#snippet icon()}
							<i class="fa-solid fa-gear {iconClass}"></i>
						{/snippet}
					</SidebarItem>
				</SidebarGroup>
			{/if}

			<!-- Identity sits at the foot of the sidebar, matching the shared
			     templates: the nav is what the user scans, the account is what they
			     reach for last. `minimal` keeps it to a single avatar row, which is
			     all a 250px column has space for. -->
			{#if ($isAuthorized || $isLoggingOut) && !$isSidebarRail}
				<div class="my-4">
					<AbstractIdCard
						minimal
						compact
						showLinks={false}
						onclick={() => push($unreadNotificationCount > 0 ? '/Settings/Account/Notifications' : '/Settings')}
					/>
				</div>
			{:else}
				<!-- The card carries the gap between the nav and the controls below
				     it. When it isn't rendered — rail mode, or signed out — this
				     stands in, otherwise the theme button crowds the last nav icon.
				     An explicit height rather than a margin, which would collapse on
				     an empty div. -->
				<div class="h-8" aria-hidden="true"></div>
			{/if}
		</SidebarWrapper>
	</Sidebar>

	<div class="mt-8 text-center text-sm text-gray-600 dark:text-gray-500">
		<!-- The `!` on the padding is load-bearing: flowbite's Button carries
		     `px-5`, and Tailwind emits the shorthand `.p-2` BEFORE the directional
		     `.px-5`, so the plain form loses on source order however the class
		     attribute is written — leaving a button wider than the rail with its
		     icon off the centre line. `!p-2` keeps a real hit target while still
		     fitting the strip. -->
		<div class="mb-4 flex flex-wrap items-center justify-center gap-2" class:flex-col={$isSidebarRail}>
			<LanguageSelector buttonClass="h-9 p-2 dark:!bg-transparent" />
			<ThemeSwitcherButton
				class={$isSidebarRail
					? 'w-full border-none bg-transparent !p-2 dark:!bg-transparent'
					: '!p-2 dark:!bg-transparent'}
				iconClass="h-4 w-4"
			/>
		</div>
		<p class:hidden={$isSidebarRail}>
			<a
				class="font-bold text-gray-700 hover:text-black hover:underline dark:text-gray-400 dark:hover:text-white"
				href="/Manage/">{site.shortname}</a
			>
			{$t('is an')}
			<a
				class="font-bold text-gray-700 hover:text-black hover:underline dark:text-gray-400 dark:hover:text-white"
				href="https://abstract3d.com"
				target="_blank">Abstract</a
			>
			{$t('product.')}<br />{$t('All rights reserved.')}
		</p>

		<div>
			<a href="https://abstract3d.com" target="_blank">
				<img
					class="{$isSidebarRail
						? 'max-w-[24px]'
						: 'max-w-[48px]'} mt-4 inline rounded-sm opacity-70 hover:opacity-100 dark:opacity-30 dark:invert hover:dark:opacity-100"
					src="/Abstract-LogoIcon-Black-Transparent.png"
					alt="Abstract"
				/>
			</a>
		</div>

		<div class="mt-4 flex justify-center gap-2" class:hidden={$isSidebarRail}>
			<a href="https://x.com/abstract3d" title={$t('Follow Abstract on Twitter')} target="_blank">
				<svg
					class="inline h-4 w-4 text-gray-500 hover:text-black dark:text-gray-600 dark:hover:text-white"
					aria-hidden="true"
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 20 20"
				>
					<path
						fill="currentColor"
						d="M12.186 8.672 18.743.947h-2.927l-5.005 5.9-4.44-5.9H0l7.434 9.876-6.986 8.23h2.927l5.434-6.4 4.82 6.4H20L12.186 8.672Zm-2.267 2.671L8.544 9.515 3.2 2.42h2.2l4.312 5.719 1.375 1.828 5.731 7.613h-2.2l-4.699-6.237Z"
					/>
				</svg>
			</a>

			<a href="https://discord.gg/ra5cCvf52s" title={$t('Join Abstract on Discord')} target="_blank">
				<svg
					class="inline h-5 w-5 text-gray-500 hover:text-black dark:text-gray-600 dark:hover:text-white"
					aria-hidden="true"
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 24 24"
				>
					<path
						fill="currentColor"
						d="M18.942 5.556a16.3 16.3 0 0 0-4.126-1.3 12.04 12.04 0 0 0-.529 1.1 15.175 15.175 0 0 0-4.573 0 11.586 11.586 0 0 0-.535-1.1 16.274 16.274 0 0 0-4.129 1.3 17.392 17.392 0 0 0-2.868 11.662 15.785 15.785 0 0 0 4.963 2.521c.41-.564.773-1.16 1.084-1.785a10.638 10.638 0 0 1-1.706-.83c.143-.106.283-.217.418-.331a11.664 11.664 0 0 0 10.118 0c.137.114.277.225.418.331-.544.328-1.116.606-1.71.832a12.58 12.58 0 0 0 1.084 1.785 16.46 16.46 0 0 0 5.064-2.595 17.286 17.286 0 0 0-2.973-11.59ZM8.678 14.813a1.94 1.94 0 0 1-1.8-2.045 1.93 1.93 0 0 1 1.8-2.047 1.918 1.918 0 0 1 1.8 2.047 1.929 1.929 0 0 1-1.8 2.045Zm6.644 0a1.94 1.94 0 0 1-1.8-2.045 1.93 1.93 0 0 1 1.8-2.047 1.919 1.919 0 0 1 1.8 2.047 1.93 1.93 0 0 1-1.8 2.045Z"
					/>
				</svg>
			</a>
		</div>
	</div>
</Drawer>

<div class="mx-0 flex w-full max-w-screen-2xl px-4">
	<main
		class="mx-auto mt-10 mb-40 w-full {$isSidebarRail
			? 'sm:ml-[85px] xl:ml-[85px]'
			: 'sm:ml-64 xl:ml-72'} dark:text-white"
	>
		{#if children}{@render children()}{:else if (hasToken || !$isAuthorizationPending) && $isConfigurationLoaded}
			<Router {routes} restoreScrollState={true} onconditionsfailed={() => push('/')} />
		{:else if ($configurationError ?? '').length > 0}
			<Alert>
				<Heading tag="h1" class="text-2xl font-bold">
					<i class="fa-solid fa-bug mr-2 inline pb-3 text-5xl"></i>{$t('No Backend Response')}
				</Heading>
				<P>
					{$t('{{name}} is currently unavailable. Please try again later.', { name: site.name })}
				</P>
			</Alert>
		{:else}
			<Spinner />
		{/if}
	</main>
</div>

<SvelteToast />
